import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OSS_SESSIONS_KEY = "oss_sessions";
export const OSS_ACTIVE_ID_KEY = "oss_active_id";

// Legacy keys from the old single-session storage — removed on first load
const LEGACY_TOKEN_KEY = "oss_token";
const LEGACY_USER_KEY = "oss_user";

export const OssAuthContext = createContext(null);

function loadSessionsFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(OSS_SESSIONS_KEY) || "[]");
  } catch {
    return [];
  }
}

/** Identifier usabile per login password (email o codice). */
export function ossSessionIdentifier(session) {
  if (!session) return "";
  return (session.email || "").trim() || (session.login_code || "").trim() || "";
}

export function OssAuthProvider({ children }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(() => loadSessionsFromStorage());
  const [activeId, setActiveId] = useState(
    () => localStorage.getItem(OSS_ACTIVE_ID_KEY) || null
  );
  const [loading, setLoading] = useState(true);

  // Always-fresh refs to avoid stale closures in callbacks and interceptor
  const sessionsRef = useRef(sessions);
  const activeIdRef = useRef(activeId);
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const activeOss = sessions.find((s) => s.id === activeId) || null;
  const token = activeOss?.token || null;

  // Persist sessions and active id whenever they change
  useEffect(() => {
    localStorage.setItem(OSS_SESSIONS_KEY, JSON.stringify(sessions));
  }, [sessions]);

  useEffect(() => {
    if (activeId) {
      localStorage.setItem(OSS_ACTIVE_ID_KEY, activeId);
    } else {
      localStorage.removeItem(OSS_ACTIVE_ID_KEY);
    }
  }, [activeId]);

  // On mount: clean up legacy keys, then validate / silently refresh the active session
  useEffect(() => {
    // Remove old single-session keys (one-time migration)
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);

    const storedSessions = loadSessionsFromStorage();
    const storedActiveId = localStorage.getItem(OSS_ACTIVE_ID_KEY);

    if (storedSessions.length === 0) {
      setLoading(false);
      return;
    }

    const targetSession =
      storedSessions.find((s) => s.id === storedActiveId) || storedSessions[0];

    const tryValidate = async () => {
      try {
        const { data: me } = await axios.get(`${API_URL}/api/oss/me`, {
          headers: { Authorization: `Bearer ${targetSession.token}` },
        });
        // Token still valid — restore state and refresh profilo (es. responsabile)
        const refreshed = storedSessions.map((s) =>
          s.id === targetSession.id
            ? {
                ...s,
                ...me,
                token: s.token,
                device_token: s.device_token,
                login_code: me.login_code ?? s.login_code,
              }
            : s
        );
        setSessions(refreshed);
        setActiveId(targetSession.id);
      } catch (err) {
        if (err.response?.status === 401 && targetSession.device_token) {
          // JWT expired — try silent refresh via device token (solo se già sessione attiva)
          try {
            const { data } = await axios.post(
              `${API_URL}/api/oss/auth/device-login`,
              { device_token: targetSession.device_token }
            );
            const updated = storedSessions.map((s) =>
              s.id === targetSession.id
                ? {
                    ...s,
                    token: data.token,
                    ...data.oss,
                    login_code: data.oss?.login_code ?? s.login_code,
                  }
                : s
            );
            setSessions(updated);
            setActiveId(targetSession.id);
          } catch {
            // Device login failed — remove this session
            const remaining = storedSessions.filter(
              (s) => s.id !== targetSession.id
            );
            setSessions(remaining);
            setActiveId(remaining.length > 0 ? remaining[0].id : null);
          }
        } else {
          // Network error or no device token — keep as-is
          setSessions(storedSessions);
          setActiveId(targetSession.id);
        }
      } finally {
        setLoading(false);
      }
    };

    tryValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Axios interceptor: silently refresh token on 401 for OSS API calls
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const url = err.config?.url || "";
        const isOssApi =
          url.includes("/api/oss/") &&
          !url.includes("/oss/auth/login") &&
          !url.includes("/oss/auth/device-login") &&
          !url.includes("/oss/auth/device-logout");

        if (err.response?.status === 401 && isOssApi && !err.config._ossRetry) {
          const activeSess = sessionsRef.current.find(
            (s) => s.id === activeIdRef.current
          );
          if (activeSess?.device_token) {
            err.config._ossRetry = true;
            try {
              const { data } = await axios.post(
                `${API_URL}/api/oss/auth/device-login`,
                { device_token: activeSess.device_token }
              );
              setSessions((prev) =>
                prev.map((s) =>
                  s.id === activeSess.id
                    ? {
                        ...s,
                        token: data.token,
                        ...data.oss,
                        login_code: data.oss?.login_code ?? s.login_code,
                      }
                    : s
                )
              );
              err.config.headers["Authorization"] = `Bearer ${data.token}`;
              return axios(err.config);
            } catch {
              // Device login failed — fall through to rejection
            }
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Login con email oppure codice univoco + password. Aggiunge o sostituisce la sessione e la attiva. */
  const loginWithPassword = useCallback(async (identifier, password) => {
    const { data } = await axios.post(`${API_URL}/api/oss/auth/login`, {
      email: identifier,
      password,
    });
    const newSession = {
      ...data.oss,
      device_token: data.device_token,
      token: data.token,
    };
    setSessions((prev) => {
      const without = prev.filter((s) => s.id !== newSession.id);
      return [...without, newSession];
    });
    setActiveId(newSession.id);
    return newSession;
  }, []);

  /**
   * Cambia utente attivo.
   * Verso un OSS responsabile: richiede sempre password (niente switch silenzioso / device-login).
   * Verso OSS normale: JWT o device-login silenzioso.
   * @returns {{ ok: true } | { needsPassword: true, session } | { ok: false, error }}
   */
  const switchTo = useCallback(async (ossId, { password } = {}) => {
    const targetSession = sessionsRef.current.find((s) => s.id === ossId);
    if (!targetSession) return { ok: false, error: "Sessione non trovata" };

    if (targetSession.responsabile) {
      if (!password) {
        return { needsPassword: true, session: targetSession };
      }
      const identifier = ossSessionIdentifier(targetSession);
      if (!identifier) {
        toast.error("Impossibile identificare l'OSS responsabile. Accedi dal form completo.");
        return { ok: false, error: "missing_identifier" };
      }
      try {
        await loginWithPassword(identifier, password);
        navigate("/oss", { replace: true });
        return { ok: true };
      } catch (error) {
        const detail = error.response?.data?.detail || "Password non valida";
        toast.error(detail);
        return { ok: false, error: detail };
      }
    }

    try {
      await axios.get(`${API_URL}/api/oss/me`, {
        headers: { Authorization: `Bearer ${targetSession.token}` },
      });
      setActiveId(ossId);
      navigate("/oss", { replace: true });
      return { ok: true };
    } catch (err) {
      if (err.response?.status === 401 && targetSession.device_token) {
        try {
          const { data } = await axios.post(
            `${API_URL}/api/oss/auth/device-login`,
            { device_token: targetSession.device_token }
          );
          setSessions((prev) =>
            prev.map((s) =>
              s.id === ossId
                ? {
                    ...s,
                    token: data.token,
                    ...data.oss,
                    login_code: data.oss?.login_code ?? s.login_code,
                  }
                : s
            )
          );
          setActiveId(ossId);
          navigate("/oss", { replace: true });
          return { ok: true };
        } catch {
          toast.error("Sessione scaduta, accedi di nuovo con la password.");
          setSessions((prev) => prev.filter((s) => s.id !== ossId));
          return { ok: false, error: "session_expired" };
        }
      }
      return { ok: false, error: "switch_failed" };
    }
  }, [navigate, loginWithPassword]);

  /** Logout dell'utente attivo: revoca device token e attiva il prossimo o reindirizza al login. */
  const logoutActive = useCallback(async () => {
    const current = sessionsRef.current.find(
      (s) => s.id === activeIdRef.current
    );
    if (current?.device_token) {
      try {
        await axios.post(`${API_URL}/api/oss/auth/device-logout`, {
          device_token: current.device_token,
        });
      } catch {
        // ignore — revoca best-effort
      }
    }
    const remaining = sessionsRef.current.filter(
      (s) => s.id !== activeIdRef.current
    );
    setSessions(remaining);
    // Non attivare in silenzio un responsabile: richiede password
    const nextNormal = remaining.find((s) => !s.responsabile);
    if (nextNormal) {
      setActiveId(nextNormal.id);
      navigate("/oss", { replace: true });
    } else if (remaining.length > 0) {
      setActiveId(null);
      navigate("/oss-login", { replace: true });
    } else {
      setActiveId(null);
      navigate("/oss-login", { replace: true });
    }
  }, [navigate]);

  /** Naviga alla pagina di login per aggiungere un altro utente al dispositivo. */
  const addAnotherUser = useCallback(() => {
    navigate("/oss-login?add=1");
  }, [navigate]);

  return (
    <OssAuthContext.Provider
      value={{
        sessions,
        activeOss,
        token,
        loading,
        loginWithPassword,
        switchTo,
        logoutActive,
        addAnotherUser,
      }}
    >
      {children}
    </OssAuthContext.Provider>
  );
}

export function useOssAuth() {
  const ctx = useContext(OssAuthContext);
  if (!ctx) throw new Error("useOssAuth must be used within OssAuthProvider");
  return ctx;
}
